import React, { useCallback, useEffect, useMemo, useRef } from "react";

import type { Handler } from "@use-gesture/react";
import { useDashboardContext } from "../../../../contexts/dashboard-context";
import { useClient } from "../../../../hooks/useClient";
import { useSelectedZone, useLogicalSelectedPage } from "../../../../hooks/useSelection";
import { IPanelSymbol, ISymbolsListPanelRef, SymbolsListPanel } from "./symbols-list-panel";
import { useSymbols } from "../../../../hooks/useSymbols";
import { useSelectedSymbol } from "./use-selected-symbol";
import { DragImage, IDragImageRef } from "../../../drag-image";
import { usePreviewContext } from "../../../../contexts/preview-context";
import { useEditor } from "../../../../hooks/useEditor";
import { getCenterOfElement } from "../../../../utils/dom";
import { transformPoint } from "../../../../transform";

export function SymbolsPanelContainer() {
	const dashboard = useDashboardContext();
	const selectedZone = useSelectedZone();
	const { data: symbols } = useSymbols();
	const client = useClient();
	const currentSymbol = useSelectedSymbol(selectedZone);
	const page = useLogicalSelectedPage();
	const editor = useEditor();
	const { getScreenToSpreadTransform, getZonesAtPoint, previewOverlayRef, getCenterOfZone } =
		usePreviewContext();
	const panelRef = useRef<ISymbolsListPanelRef>(null);
	const dragImageRef = useRef<IDragImageRef>(null);
	const lastHighlightedZoneRef = useRef<eXpress.core.IZone | undefined>(undefined);

	const onSymbolClick = useCallback(
		(code: string) => {
			const newSymbol = symbols?.find((sym) => sym.code === code);
			if (!newSymbol) return;
			if (newSymbol === currentSymbol) return;
			if (dashboard.canReplaceSymbol(selectedZone, false)) {
				// Replace Symbol
				dashboard.replaceSymbol(selectedZone, newSymbol, false);
			} else if (dashboard.canAddSymbolZone(selectedZone?.page)) {
				// Add Symbol
				dashboard.addSymbolZone({
					symbol: newSymbol,
					page: selectedZone?.page, // If no zone selected the default page is used
				});
			}
			// Else: panel was shown but user couldn't add/replace symbols,
		},
		[currentSymbol, dashboard, selectedZone, symbols],
	);

	const onDrag = useCallback<Handler<"drag">>(
		({ last, args, xy: [x, y], tap, dragging, event, first }) => {
			const [symbol] = (args as IPanelSymbol[]) || [];

			if (!symbol) return;

			// ignore first event because we need to wait for a possible tap
			if (first) return;

			// tap happened - only call onSymbolClick if we're not in a drag state
			if (tap) {
				onSymbolClick(symbol.code);
				return;
			}

			const lastHighlightedZone = lastHighlightedZoneRef?.current;
			const dragImage = dragImageRef?.current;

			const validDropZone = getZonesAtPoint(
				{ x, y },
				(zone) => eXpress.core.isSymbolZone(zone) && dashboard.canReplaceSymbol(zone, true),
			).last() as eXpress.core.SymbolZone | undefined;

			// regular drag event
			if (dragging) {
				// drag hasnt been started yet
				if (!dragImage?.isDragging()) {
					const thumbUrl = symbol.url;
					if (thumbUrl) {
						const dragImageEl = event?.target as unknown as HTMLImageElement;
						if (!dragImageEl.classList.contains("symbols-list-panel__img")) {
							return;
						}
						const center = getCenterOfElement(dragImageEl);
						if (center) {
							dragImage?.start(thumbUrl, x, y, center.left - x, center.top - y);
						} else if (thumbUrl) {
							dragImage?.start(thumbUrl, x, y);
						}
					}
				} else {
					// just dragging, move drag
					dragImage?.moveTo(x, y);

					// highlight target if hovering validDropZone
					if (validDropZone !== lastHighlightedZone) {
						previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
							highlightZone: false,
						});
						previewOverlayRef.current?.updateZoneState(validDropZone, {
							highlightZone: true,
						});
						lastHighlightedZoneRef.current = validDropZone;
					}
				}
				return;
			}

			// released drag
			if (last) {
				let isApplied = false;

				// check if dropped outside of the list panel
				const panelEl = panelRef.current?.getSymbolSelectionPanelEl();
				const isOutsidePanel = panelEl
					? !document.elementsFromPoint(x, y).includes(panelEl)
					: false;

				const newSymbol = symbols?.find((sym) => sym.code === symbol.code);

				const currentHoveredSymbol = eXpress.core.isSymbolZone(validDropZone)
					? validDropZone.symbol
					: undefined;

				try {
					// replace symbol if hovering a valid symbol zone
					if (
						validDropZone &&
						dashboard.canReplaceSymbol(validDropZone, true) &&
						newSymbol &&
						currentHoveredSymbol !== newSymbol
					) {
						isApplied = !!dashboard.replaceSymbol(validDropZone, newSymbol, true);
						editor.selection.setCurrentZoneAndBroadcast(validDropZone);
					} else if (
						// else if outside panel and hovering page: add symbol to page
						page &&
						dashboard.canAddSymbolZone(page) &&
						newSymbol &&
						isOutsidePanel
					) {
						const centerPoint = eXpress.math.point(x, y);
						const hoveredPageZone = getZonesAtPoint(
							{ x: centerPoint.left, y: centerPoint.top },
							(zone) => eXpress.core.isPageZone(zone),
						).first();

						if (!hoveredPageZone) return;

						const transform = getScreenToSpreadTransform(
							hoveredPageZone.spread,
							editor.logger,
						);

						if (!transform) return;

						const newPoint = transformPoint(centerPoint, transform);

						isApplied = !!dashboard.addSymbolZone({
							symbol: newSymbol,
							page: hoveredPageZone as eXpress.core.IPageZone,
							hiResCenter: newPoint,
						});
					}
				} finally {
					// Remove DragImage and flyback animation
					const flyToPosition = isApplied
						? getCenterOfZone(validDropZone)
						: getCenterOfElement(event?.target as unknown as HTMLImageElement);

					if (flyToPosition) {
						dragImage?.end(flyToPosition.left - x, flyToPosition.top - y);
					} else {
						dragImage?.end();
					}
				}

				// remove highlight
				previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
					highlightZone: false,
				});
				lastHighlightedZoneRef.current = undefined;
			}
		},
		[
			lastHighlightedZoneRef,
			previewOverlayRef,
			dashboard,
			symbols,
			getZonesAtPoint,
			getCenterOfZone,
			page,
			editor,
			getScreenToSpreadTransform,
			onSymbolClick,
		],
	);

	const mappedSymbols = useMemo<IPanelSymbol[]>(() => {
		if (!symbols) return [];
		return symbols
			.map((symbol) => ({
				code: symbol.code,
				url: symbol.thumbUrl?.getAbsoluteUrl(client),
				isWhite: symbol.isWhite,
			}))
			.filter((symbol): symbol is IPanelSymbol => !!symbol.url);
	}, [client, symbols]);

	// Scroll into view if selection is changed (animated)
	useEffect(() => {
		// scroll selection into view if a new symbol is selected
		if (panelRef.current && currentSymbol)
			panelRef.current.scrollIntoView(currentSymbol.code, true);
	}, [currentSymbol]);

	// Scroll into view if symbols list is loaded (not animated)
	useEffect(() => {
		if (panelRef.current && currentSymbol) {
			// This will override the animation
			panelRef.current.scrollIntoView(currentSymbol.code, false);
		}
		// eslint-disable-next-line react-compiler/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!symbols) return null;

	return (
		<>
			<DragImage ref={dragImageRef} />
			<SymbolsListPanel
				selectedSymbolCode={currentSymbol?.code}
				symbols={mappedSymbols}
				ref={panelRef}
				onClick={onSymbolClick}
				onDrag={onDrag}
			/>
		</>
	);
}
